import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { configureApi } from '@/api'
import { loadRuntimeConfig, type AppConfig } from './runtime'

const ConfigContext = createContext<AppConfig | null>(null)

export function useConfig(): AppConfig {
  const config = useContext(ConfigContext)
  if (!config) throw new Error('useConfig must be used inside <ConfigProvider>')
  return config
}

/**
 * Boot gate. Nothing below this renders until `/config.json` has resolved,
 * because the Auth0 client cannot be constructed without a domain and client id,
 * and a provider that swaps its configuration mid-session loses the session.
 *
 * A failure here is fatal and says so. The alternative — degrading to the mock
 * adapter — would be a deployed build serving fabricated data behind no login,
 * which is worse than a visible error.
 */
export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadRuntimeConfig(controller.signal)
      .then((loaded) => {
        // Before setConfig: nothing below renders, so nothing queries, until the backend is chosen.
        configureApi(loaded)
        setConfig(loaded)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : String(cause))
      })
    return () => controller.abort()
  }, [])

  if (error) return <ConfigFailure detail={error} />

  // Deliberately blank rather than a skeleton. This resolves in one same-origin
  // round trip against a no-cache object at the edge; a spinner that appears for
  // 40ms reads as a flash.
  if (!config) return null

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
}

function ConfigFailure({ detail }: { detail: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6">
      <div className="w-full max-w-[460px] space-y-3 rounded-lg border border-rule bg-surface p-6">
        <h1 className="font-head text-[17px] leading-6">This portal is not configured</h1>
        <p className="text-[13px] leading-5 text-ink-muted">
          The application loaded but could not read its deployment configuration, so it cannot
          reach the sign-in service. This is a deployment fault, not something retrying will
          fix. Send the detail below to whoever deployed it.
        </p>
        <pre className="overflow-x-auto rounded border border-rule bg-surface-sunk p-3 text-[12px] leading-4 text-ink-muted">
          {detail}
        </pre>
      </div>
    </div>
  )
}
